import React, {Component, ReactNode} from "react"
import PropTypes from "prop-types"
import id from "@irati/utils/id"
import {get, isEqual} from "lodash"

import {getResolvedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import ValidationInput, {ValidationState, ValidationProps} from "@irati/utils/validation/Input"
import Spacing from "../../Spacing"
import RadioOption from "./RadioOption"
import {RadioGroupLabel, RadioGroupError} from "./styled"
import Flex from "@irati/ui/src/Flex"

export interface Option {
  label: string
  value: any
}

export interface OptionRow {
  option: Option
  checked: boolean
  disabled: boolean
}

interface Props extends ValidationProps, InjectIntl {
  name: string
  value: any
  options: Option[]
  onChange: (value: any, option?: Option) => void
  inline?: boolean
  label?: string
  className?: string
  disabled?: boolean
  whiteSpace?: string
  rowRenderer?: (optionRow: OptionRow) => ReactNode
  flush?: boolean
  // Mainly used for RadioFilter
  equateBy?: string
  // Typically left to default may need to be changed if custom renderer used
  justifyRadios?: "flex-start" | "flex-end" | "center" | "space-around" | "space-between"
  // Typically 3 to default may need to be changed if custom renderer used
  gapRadios?: number
}
interface State extends ValidationState {}

class Radio extends Component<Props, State> {
  validation = new ValidationInput(this)
  state: State = {
    errorMsg: undefined
  }

  static contextTypes = {
    currentScreen: PropTypes.string
  }

  static getDerivedStateFromProps(nextProps: Props, prevState: State) {
    if (nextProps.value !== prevState.value) {
      return {value: nextProps.value}
    } else return null
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    this.validation.update(prevProps, prevState)
  }

  componentWillUnmount() {
    this.validation.destroy()
  }

  handleChange = (index: number) => {
    const {onChange, options} = this.props
    const newValue = options[index].value
    onChange(options[index].value, options[index])
    this.setState({
      value: newValue
    })
  }

  compareValueToOption = (value: any, option: Option) => {
    const {equateBy} = this.props
    if (equateBy) {
      return isEqual(get(value, equateBy), get(option, equateBy))
    }
    return isEqual(value, option.value)
  }

  render() {
    const {name, options, className, disabled, label, inline, flush, rowRenderer, justifyRadios, whiteSpace, gapRadios} = this.props
    const {errorMsg, value} = this.state
    const idNamespace = id("radioGroup", name)
    return (
      <Spacing marginBottom={flush || rowRenderer ? 0 : 2}>
        {label && (
          <RadioGroupLabel id={id("label", idNamespace)} error={!!errorMsg}>
            {getResolvedMessage(this, "label")}
          </RadioGroupLabel>
        )}
        <Flex justifyContent={justifyRadios || "space-between"} wrap="wrap" gap={gapRadios || 3}>
          {options.map((option, index) => (
            <RadioOption
              id={id(`${index}_`, idNamespace)}
              name={name}
              key={index}
              value={index}
              option={option}
              onChange={this.handleChange}
              checked={this.compareValueToOption(value, option)}
              inline={!!inline}
              disabled={!!disabled}
              className={className}
              whiteSpace={whiteSpace}
              rowRenderer={rowRenderer}
            />
          ))}
        </Flex>
        {errorMsg && <RadioGroupError small>{getResolvedMessage(this, "errorMsg", this.state)}</RadioGroupError>}
      </Spacing>
    )
  }
}

export default injectIntl(Radio)
